import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import type { PaginationState } from "./usePagination";

export type CustomerType = "general_contractor" | "property_manager" | "owner" | "insurance_company" | "other";
export type QBSyncStatus = "not_synced" | "pending" | "synced" | "error";

export interface Customer {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  contact_name: string | null;
  customer_type: string | null;
  billing_address: string | null;
  billing_city: string | null;
  billing_state: string | null;
  billing_zip: string | null;
  job_address: string | null;
  job_city: string | null;
  job_state: string | null;
  job_zip: string | null;
  qb_customer_id: string | null;
  qb_sync_status: string | null;
  qb_synced_at: string | null;
  notes: string | null;
  logo_url: string | null;
  logo_path: string | null;
  created_at: string;
  is_deleted: boolean;
}

export const useCustomers = (pagination?: PaginationState) => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["customers", pagination?.page],
    queryFn: async () => {
      if (pagination) {
        const { count } = await supabase
          .from("customers")
          .select("id", { count: "exact", head: true })
          .eq("is_deleted", false);
        pagination.setTotalCount(count ?? 0);
      }

      let query = supabase
        .from("customers")
        .select("*")
        .eq("is_deleted", false)
        .order("name");

      if (pagination) {
        query = query.range(pagination.from, pagination.to);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as Customer[];
    },
    enabled: !!user,
  });
};

export interface CreateCustomerInput {
  name: string;
  contact_name?: string;
  email?: string;
  phone?: string;
  customer_type?: CustomerType;
  billing_address?: string;
  billing_city?: string;
  billing_state?: string;
  billing_zip?: string;
  job_address?: string;
  job_city?: string;
  job_state?: string;
  job_zip?: string;
  notes?: string;
}

export const useCreateCustomer = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateCustomerInput) => {
      const { data: companyId, error: cidErr } = await supabase.rpc("my_company_id");
      if (cidErr) throw cidErr;

      const { data, error } = await supabase
        .from("customers")
        .insert({
          company_id: companyId,
          name: input.name,
          contact_name: input.contact_name || null,
          email: input.email || null,
          phone: input.phone || null,
          customer_type: input.customer_type || "general_contractor",
          billing_address: input.billing_address || null,
          billing_city: input.billing_city || null,
          billing_state: input.billing_state || null,
          billing_zip: input.billing_zip || null,
          job_address: input.job_address || null,
          job_city: input.job_city || null,
          job_state: input.job_state || null,
          job_zip: input.job_zip || null,
          notes: input.notes || null,
          qb_sync_status: "pending",
        })
        .select("id, name")
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["customers"] });
    },
  });
};

export const useUpdateCustomer = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...input }: CreateCustomerInput & { id: string }) => {
      const { error } = await supabase
        .from("customers")
        .update({
          name: input.name,
          contact_name: input.contact_name || null,
          email: input.email || null,
          phone: input.phone || null,
          customer_type: input.customer_type || "general_contractor",
          billing_address: input.billing_address || null,
          billing_city: input.billing_city || null,
          billing_state: input.billing_state || null,
          billing_zip: input.billing_zip || null,
          job_address: input.job_address || null,
          job_city: input.job_city || null,
          job_state: input.job_state || null,
          job_zip: input.job_zip || null,
          notes: input.notes || null,
        })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["customers"] });
    },
  });
};

export const useSoftDeleteCustomer = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { count } = await supabase
        .from("projects")
        .select("id", { count: "exact", head: true })
        .eq("customer_id", id);
      if (count && count > 0) throw new Error("Customer has active projects — cannot delete");

      const { error } = await supabase
        .from("customers")
        .update({ is_deleted: true })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["customers"] });
    },
  });
};
